import {
  Grid,
  LinearProgress,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import TodayOrders from "./widgets/todayOrders";
import WeekSummary from "./widgets/weekSummary";
import {
  getDsokDashboardStats,
  getDsokYearSummary,
} from "app/services/api/menu";
import YearSummary from "./widgets/yearSummary";
import { useTranslation } from "react-i18next";
import DashboardButtons from "./widgets/dashboardButtons";
import KioskMaintenanceWidget from "./widgets/kioskMaintenance";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { getBEUrl } from "app/services/api/management";
import {
  exportCustomerMenu,
  exportSmartkioskMenu,
} from "app/services/api/integrations";
import { handleImportDotykacka as importDotykackaHandler } from "app/integrations/dotykacka";
import { handleImportWoocommerce as importWoocommerceHandler } from "app/integrations/woocommerce";
import { handleImportIkelp as importIkelpHandler } from "app/integrations/ikelp";
import {
  handleOberonImport,
  handleImportOberon as importOberonHandler,
  fetchOberonSyncStatus,
  isTerminalStatus,
  isSuccessStatus,
  cancelOberonJob,
} from "app/integrations/oberon";
import { syncOberonDelta, syncOberonViaDla } from "app/services/api/integrations";
import { useSnackbar } from "notistack";

const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_CONSECUTIVE_FAILURES = 3;
const OBERON_SYNC_STORAGE_KEY = "oberonSyncJobId";
const OBERON_SYNC_START_TIME_KEY = "oberonSyncStartTime";
const OBERON_SYNC_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

const DsokWidgets = () => {
  const [data, setData] = useState(null);
  const [yearSummary, setYearSummary] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [exportProgress, setExportProgress] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // OBERON sync state
  const [oberonSyncJobId, setOberonSyncJobId] = useState(null);
  const [isOberonSyncRunning, setIsOberonSyncRunning] = useState(false);
  const [oberonSyncMessage, setOberonSyncMessage] = useState("Prebieha...");
  const [oberonSyncCompleted, setOberonSyncCompleted] = useState(null); // null | 'success' | 'error'
  const [oberonSyncStartTime, setOberonSyncStartTime] = useState(null); // Local start time for timeout
  // Import summary modal (shown on successful completion)
  const [importSummary, setImportSummary] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const oberonPollingRef = useRef(null);
  const oberonCompletionTimeoutRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const customer = user?.customer || {};
  const dsokType = user?.customer?.dsokType || "gastrokiosk";
  const [currencyCode] = useState(
    () => customer?.dsokSettings?.customer?.currencyCode || "€",
  );
  let dataVendor = "none";
  if (dsokType === "smartkiosk") {
    dataVendor =
      customer?.dsokSettings?.integrations?.orderManagement?.type || "none";
  } else {
    dataVendor = customer?.dsokSettings?.orderManagement?.type || "none";
  }

  // Product import vendor (separate from orderManagement). WooCommerce is products-only.
  const productsVendor =
    customer?.dsokSettings?.integrations?.productsManagement?.type || "none";

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Cleanup polling and completion timeout on unmount
  useEffect(() => {
    return () => {
      if (oberonPollingRef.current) {
        clearInterval(oberonPollingRef.current);
        oberonPollingRef.current = null;
      }
      if (oberonCompletionTimeoutRef.current) {
        clearTimeout(oberonCompletionTimeoutRef.current);
        oberonCompletionTimeoutRef.current = null;
      }
    };
  }, []);

  // Recover sync state from localStorage on mount
  useEffect(() => {
    const savedJobId = localStorage.getItem(OBERON_SYNC_STORAGE_KEY);
    const savedStartTime = parseInt(
      localStorage.getItem(OBERON_SYNC_START_TIME_KEY) || "0",
      10,
    );

    if (savedJobId && !oberonSyncJobId && !isOberonSyncRunning) {
      // Check if the job is still running
      const checkSavedJob = async () => {
        try {
          const { status, message } = await fetchOberonSyncStatus(savedJobId);

          if (isTerminalStatus(status)) {
            // Job already completed, clear storage
            localStorage.removeItem(OBERON_SYNC_STORAGE_KEY);
            localStorage.removeItem(OBERON_SYNC_START_TIME_KEY);
          } else {
            // Job still running, resume polling
            setOberonSyncJobId(savedJobId);
            setOberonSyncMessage(message || "Prebieha...");
            setOberonSyncStartTime(savedStartTime || Date.now());
            setIsOberonSyncRunning(true);
          }
        } catch (e) {
          // Failed to check status, clear storage
          localStorage.removeItem(OBERON_SYNC_STORAGE_KEY);
          localStorage.removeItem(OBERON_SYNC_START_TIME_KEY);
        }
      };

      checkSavedJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear all OBERON sync state
  const clearOberonSyncState = useCallback(() => {
    setIsOberonSyncRunning(false);
    setOberonSyncJobId(null);
    setOberonSyncCompleted(null);
    setOberonSyncMessage("Prebieha...");
    setOberonSyncStartTime(null);
    consecutiveFailuresRef.current = 0;
    // Remove from localStorage
    localStorage.removeItem(OBERON_SYNC_STORAGE_KEY);
    localStorage.removeItem(OBERON_SYNC_START_TIME_KEY);
  }, []);

  // Stop polling helper (does not clear running state - used when transitioning to completed)
  const stopOberonPolling = useCallback(() => {
    if (oberonPollingRef.current) {
      clearInterval(oberonPollingRef.current);
      oberonPollingRef.current = null;
    }
    setOberonSyncJobId(null);
    consecutiveFailuresRef.current = 0;
    // Remove from localStorage when polling stops
    localStorage.removeItem(OBERON_SYNC_STORAGE_KEY);
    localStorage.removeItem(OBERON_SYNC_START_TIME_KEY);
  }, []);

  // Poll for OBERON sync status
  const pollOberonStatus = useCallback(
    async (jobId) => {
      if (!jobId) return;

      try {
        const { status, message, summary } = await fetchOberonSyncStatus(jobId);
        consecutiveFailuresRef.current = 0;

        // Check for timeout (2 minutes since job started locally)
        const startTime =
          oberonSyncStartTime ||
          parseInt(localStorage.getItem(OBERON_SYNC_START_TIME_KEY) || "0", 10);
        if (startTime > 0) {
          const now = Date.now();
          if (now - startTime > OBERON_SYNC_TIMEOUT_MS) {
            // Job has been running too long, cancel it
            try {
              await cancelOberonJob(jobId);
            } catch (cancelError) {
              // Ignore cancel errors
            }

            stopOberonPolling();
            setOberonSyncCompleted("error");
            setOberonSyncMessage(
              "Synchronizácia dát nebola úspešná, skúste to prosím ešte raz",
            );

            // Clear completion timeout if exists
            if (oberonCompletionTimeoutRef.current) {
              clearTimeout(oberonCompletionTimeoutRef.current);
            }

            // Wait 5 seconds then clear the status
            oberonCompletionTimeoutRef.current = setTimeout(() => {
              clearOberonSyncState();
            }, 5000);

            return;
          }
        }

        // Update message
        setOberonSyncMessage(message || "Prebieha...");

        // Check for terminal state
        if (isTerminalStatus(status)) {
          stopOberonPolling();

          const isSuccess = isSuccessStatus(status);
          setOberonSyncCompleted(isSuccess ? "success" : "error");
          setOberonSyncMessage(
            isSuccess
              ? "Synchronizácia dokončená"
              : message || "Synchronizácia zlyhala",
          );

          // Show a summary modal with counts on successful completion.
          if (isSuccess) {
            setImportSummary(summary || null);
            setSummaryOpen(true);
          }

          // Clear completion timeout if exists
          if (oberonCompletionTimeoutRef.current) {
            clearTimeout(oberonCompletionTimeoutRef.current);
          }

          // Wait 5 seconds then clear the status
          oberonCompletionTimeoutRef.current = setTimeout(() => {
            clearOberonSyncState();
          }, 5000);
        }
      } catch (e) {
        consecutiveFailuresRef.current += 1;

        // Update message to show there's an issue
        setOberonSyncMessage((prev) => prev + " (problém s načítaním stavu)");

        // Warn after N consecutive failures
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          enqueueSnackbar(
            "Nepodarilo sa načítať stav synchronizácie. Pokračujem v pokusoch...",
            {
              variant: "warning",
            },
          );
          consecutiveFailuresRef.current = 0; // Reset to avoid spamming
        }
      }
    },
    [
      enqueueSnackbar,
      stopOberonPolling,
      clearOberonSyncState,
      oberonSyncStartTime,
    ],
  );

  // Start polling when jobId changes
  useEffect(() => {
    if (oberonSyncJobId && isOberonSyncRunning) {
      // Clear any existing interval first
      if (oberonPollingRef.current) {
        clearInterval(oberonPollingRef.current);
      }

      // Poll immediately
      pollOberonStatus(oberonSyncJobId);

      // Then poll every POLLING_INTERVAL
      oberonPollingRef.current = setInterval(() => {
        pollOberonStatus(oberonSyncJobId);
      }, POLLING_INTERVAL);
    }

    return () => {
      if (oberonPollingRef.current && !isOberonSyncRunning) {
        clearInterval(oberonPollingRef.current);
        oberonPollingRef.current = null;
      }
    };
  }, [oberonSyncJobId, isOberonSyncRunning, pollOberonStatus]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const responseData = await getDsokDashboardStats();
        const responseYearData = await getDsokYearSummary();
        setData(responseData.kpiCards);
        // setCurrencyCode(responseData.currencyCode);
        setYearSummary(responseYearData);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  const handleExportClick = async () => {
    const dsokType = customer?.dsokType || "gastrokiosk";
    setExportOpen(true);
    setExportProgress(0);
    let current = 0;
    const interval = setInterval(() => {
      current = Math.min(90, current + Math.floor(Math.random() * 8) + 5);
      setExportProgress((prev) => Math.max(prev ?? 0, current));
    }, 250);

    try {
      let result = {};
      if (dsokType === "gastrokiosk") {
        result = await exportCustomerMenu();
      }
      if (dsokType === "smartkiosk") {
        result = await exportSmartkioskMenu();
      }

      if (result.code === 200 && result.status === "OK") {
        if (result?.integrationError?.status >= 404) {
          enqueueSnackbar(result?.integrationError?.message, {
            variant: "error",
          });
          enqueueSnackbar(
            t("pages.dsokCustomers.notific  ations.exportFailed"),
            {
              variant: "error",
            },
          );
        } else {
          enqueueSnackbar(
            t("pages.dsokCustomers.notifications.exportSuccessful"),
            {
              variant: "success",
            },
          );
        }
      } else {
        enqueueSnackbar(t("pages.dsokCustomers.notifications.exportFailed"), {
          variant: "error",
        });
      }
      clearInterval(interval);
      setExportProgress(100);
    } catch (e) {
      clearInterval(interval);
      setExportProgress(0);
    }

    setTimeout(() => {
      setExportProgress(null);
      setExportOpen(false);
    }, 500);
  };

  const handleImportDotykacka = () =>
    importDotykackaHandler({
      customer,
      setImportOpen,
      setImportProgress,
      enqueueSnackbar,
      t,
    });

  const handleImportWoocommerce = () =>
    importWoocommerceHandler({
      customer,
      setImportOpen,
      setImportProgress,
      enqueueSnackbar,
      t,
    });

  const handleImportIkelp = () =>
    importIkelpHandler({
      customer,
      setImportOpen,
      setImportProgress,
      enqueueSnackbar,
      t,
    });

  // DSOK-456: OBERON in DLA mode — trigger the on-premise DLA (no direct IP, no job
  // polling). The DLA fetches locally and pushes; there is no job to poll here.
  const oberonPm = customer?.dsokSettings?.integrations?.productsManagement;
  const isOberonDlaMode =
    oberonPm?.type === "oberon" && oberonPm?.connectionMode === "dla";
  const oberonDlaConnectionCode = oberonPm?.connectionCode;
  const oberonDlaCustomerId =
    customer?.dsokSettings?.customer?.customerId ??
    customer?.id ??
    customer?.customerId;
  const runOberonDlaSync = async (mode) => {
    if (isOberonSyncRunning) {
      enqueueSnackbar("Synchronizácia už prebieha...", { variant: "info" });
      return;
    }
    if (!oberonDlaConnectionCode) {
      enqueueSnackbar("Konfigurácia importu nie je úplná.", {
        variant: "warning",
      });
      return;
    }
    try {
      const res = await syncOberonViaDla({
        customerId: oberonDlaCustomerId,
        connectionCode: oberonDlaConnectionCode,
        mode,
      });
      // Drive the SAME progress UI as a direct import via the returned job id.
      const jobId = res?.jobId || res?.data?.jobId || null;
      if (jobId) {
        const startTime = Date.now();
        localStorage.setItem(OBERON_SYNC_STORAGE_KEY, jobId);
        localStorage.setItem(OBERON_SYNC_START_TIME_KEY, startTime.toString());
        setOberonSyncJobId(jobId);
        setOberonSyncMessage("Prebieha...");
        setOberonSyncStartTime(startTime);
        setIsOberonSyncRunning(true);
      } else {
        enqueueSnackbar("Import sa nepodarilo spustiť.", { variant: "error" });
      }
    } catch (e) {
      const reason = e?.response?.data?.reason || e?.message || "";
      // Customer-facing message only — no internal wording.
      enqueueSnackbar(
        reason === "DLA_NOT_CONNECTED"
          ? "Zariadenie na prevádzke nie je pripojené."
          : "Import sa nepodarilo spustiť.",
        { variant: "error" },
      );
    }
  };

  const handleImportOberon = () => {
    if (isOberonDlaMode) {
      runOberonDlaSync("import");
      return;
    }
    // Prevent starting new sync if one is already running
    if (isOberonSyncRunning) {
      enqueueSnackbar("Synchronizácia už prebieha...", {
        variant: "info",
      });
      return;
    }

    importOberonHandler({
      customer,
      setImportOpen,
      setImportProgress,
      setOberonSyncJobId,
      setOberonSyncMessage,
      setOberonSyncStartTime,
      setIsOberonSyncRunning,
      enqueueSnackbar,
      t,
    });
  };

  const handleImportOberonDelta = () => {
    if (isOberonDlaMode) {
      runOberonDlaSync("delta");
      return;
    }
    // Prevent starting new sync if one is already running
    if (isOberonSyncRunning) {
      enqueueSnackbar("Synchronizácia už prebieha...", {
        variant: "info",
      });
      return;
    }

    handleOberonImport({
      customer,
      setImportOpen,
      setImportProgress,
      setOberonSyncJobId,
      setOberonSyncMessage,
      setOberonSyncStartTime,
      setIsOberonSyncRunning,
      enqueueSnackbar,
      t,
      syncFunction: syncOberonDelta,
    });
  };
  let buttons = [];
  if (dsokType === "gastrokiosk" && dataVendor !== "ikelp") {
    buttons = [
      {
        title: t("widgets.dashboardButtons.dataExport"),
        icon: <DownloadIcon />,
        logo: "/images/logos/dsok/dsok-logo.png",
        onClick: handleExportClick,
      },
    ];
  }
  if (dsokType === "smartkiosk" && dataVendor === "none") {
    buttons = [
      {
        title: t("widgets.dashboardButtons.dataExport"),
        icon: <DownloadIcon />,
        logo: "/images/logos/dsok/dsok-logo.png",
        onClick: handleExportClick,
      },
    ];
  }
  if (dataVendor === "dotykacka") {
    getBEUrl();
    buttons = [
      {
        title: t("widgets.dashboardButtons.dataImport"),
        icon: <UploadIcon />,
        logo: "/images/logos/dsok/dotykacka-logo.png",
        onClick: handleImportDotykacka,
      },
      {
        title: t("widgets.dashboardButtons.dataExport"),
        icon: <DownloadIcon />,
        logo: "/images/logos/dsok/dsok-logo.png",
        onClick: handleExportClick,
      },
    ];
  }
  if (dataVendor === "oberon") {
    getBEUrl();
    buttons = [
      {
        title: "Import",
        icon: <UploadIcon />,
        logo: "/images/logos/dsok/oberon-logo.png",
        onClick: handleImportOberon,
        disableOnSync: true,
      },
      {
        title: "Import zmien",
        icon: <UploadIcon />,
        onClick: handleImportOberonDelta,
        disableOnSync: true,
        groupWithPrevious: true,
      },
      {
        title: t("widgets.dashboardButtons.dataExport"),
        icon: <DownloadIcon />,
        logo: "/images/logos/dsok/dsok-logo.png",
        onClick: handleExportClick,
        disableOnSync: true,
      },
    ];
  }
  if (dataVendor === "ikelp" && dsokType === "smartkiosk") {
    getBEUrl();
    buttons = [
      {
        title: t("widgets.dashboardButtons.dataImport"),
        icon: <UploadIcon />,
        logo: "/images/logos/dsok/ikelp-logo.png",
        onClick: handleImportIkelp,
      },
      {
        title: t("widgets.dashboardButtons.dataExport"),
        icon: <DownloadIcon />,
        logo: "/images/logos/dsok/dsok-logo.png",
        onClick: handleExportClick,
      },
    ];
  }
  // WooCommerce is a products-only vendor: show its import next to Export
  // regardless of the orderManagement vendor.
  if (productsVendor === "woocommerce") {
    getBEUrl();
    buttons = [
      {
        title: t("widgets.dashboardButtons.dataImport"),
        icon: <UploadIcon />,
        logo: "/images/logos/dsok/woocommerce-logo.svg",
        onClick: handleImportWoocommerce,
      },
      {
        title: t("widgets.dashboardButtons.dataExport"),
        icon: <DownloadIcon />,
        logo: "/images/logos/dsok/dsok-logo.png",
        onClick: handleExportClick,
      },
    ];
  }
  // Sync status object for DashboardButtons
  // Calculate elapsed time for display
  const getElapsedTime = () => {
    const startTime =
      oberonSyncStartTime ||
      parseInt(localStorage.getItem(OBERON_SYNC_START_TIME_KEY) || "0", 10);
    if (!startTime) return null;
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const syncStatus =
    isOberonSyncRunning || oberonSyncCompleted
      ? {
          isRunning: isOberonSyncRunning && !oberonSyncCompleted,
          completed: oberonSyncCompleted, // 'success' | 'error' | null
          message: oberonSyncMessage,
          elapsedTime: getElapsedTime(),
        }
      : null;

  return (
    <>
      <Grid container spacing={2} alignItems={"stretch"} sx={{ mb: 2 }}>
        {buttons.length > 0 && (
          <DashboardButtons buttons={buttons} syncStatus={syncStatus} />
        )}
        <KioskMaintenanceWidget />
      </Grid>
      <Grid container spacing={2} alignItems={"stretch"}>
        <TodayOrders data={data} currencyCode={currencyCode} />
        <WeekSummary data={data} currencyCode={currencyCode} />
        <YearSummary data={yearSummary} currencyCode={currencyCode} />
      </Grid>

      {/* Import Progress Modal */}
      <Dialog
        open={Boolean(importOpen)}
        disableEscapeKeyDown
        disableAutoFocus
        sx={{ "& .MuiDialog-paper": { minWidth: 400, p: 3 } }}
      >
        <DialogContent sx={{ textAlign: "center" }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Import
          </Typography>
          <Box sx={{ width: "100%", mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, importProgress || 0))}
              sx={{
                height: 8,
                borderRadius: 4,
                "& .MuiLinearProgress-bar": { borderRadius: 4 },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {Math.round(importProgress || 0)}%
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Import Summary Modal (shown on successful completion) */}
      <Dialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        sx={{ "& .MuiDialog-paper": { minWidth: 380 } }}
      >
        <DialogTitle>Import dokončený</DialogTitle>
        <DialogContent dividers>
          {importSummary && importSummary.noChanges ? (
            <Typography>Žiadne nové zmeny — všetko je aktuálne.</Typography>
          ) : importSummary ? (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {importSummary.mode === "full" || importSummary.mode === "import"
                  ? "Plný import"
                  : "Import zmien"}
              </Typography>
              <Divider />
              {[
                ["Načítaných produktov", importSummary.received],
                ["Nových produktov", importSummary.created],
                ["Aktualizovaných produktov", importSummary.updated],
                ["Nových kategórií", importSummary.categoriesCreated],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value ?? 0}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>Import bol dokončený.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSummaryOpen(false)}>
            Zavrieť
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Progress Modal */}
      <Dialog
        open={Boolean(exportOpen)}
        disableEscapeKeyDown
        disableAutoFocus
        sx={{ "& .MuiDialog-paper": { minWidth: 400, p: 3 } }}
      >
        <DialogContent sx={{ textAlign: "center" }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Export
          </Typography>
          <Box sx={{ width: "100%", mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, exportProgress || 0))}
              sx={{
                height: 8,
                borderRadius: 4,
                "& .MuiLinearProgress-bar": { borderRadius: 4 },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {Math.round(exportProgress || 0)}%
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DsokWidgets;
